import { AdministrativeFormStateTypeI18n, EVatCustomerCategory, PaymentCondition, PaymentMethod, SupplierTypeDTO } from "../../interface/interface";
import { GetItemsProps, useApiService } from "medere-ui-framework";
import { SupplierForm } from "../../views/Supplier/SupplierModal";
import { Movement } from "../../views/Movements";
import { ComboDataDTO } from "../../dto/ComboDataDTO";

export function useSupplierService() {
  const { api, getMethod, postMethod } = useApiService();
  const baseEndpoint = "/api/secured/supplier";

  function getCities({ filterValue, offSet, pageSize }: GetItemsProps) {
    return postMethod({
      basePath: "/api/public/site",
      endpoint: "/getCities",
      body: null,
      params: {
        inputText: filterValue ?? "",
        offSet: offSet,
        pageSize: pageSize,
      }
    });
  }

  function getAllSuppliers(offSet: number, pageSize: number, params: any): Promise<any[]> {
    return postMethod<any[]>({
      basePath: baseEndpoint,
      endpoint: "/getAllSuppliers",
      body: {
        loadConfigDTO: {
          offset: offSet,
          pageSize: pageSize,
          sortingFieldInfoList: params.multiSortMeta,
          filters: params.filterDTO,
        },
        supplierStatus: params.isActive,
      },
      params: {}
    });
  }

  function getAllSuppliersCombo({ filterValue, offSet, pageSize }: GetItemsProps) {
    return getMethod({
      basePath: baseEndpoint,
      endpoint: "/getAllSuppliers",
      params: {
        filterValue: filterValue ?? "",
        offSet,
        pageSize,
      }
    });
  }

  function removeSupplier(selectedSuppliers: any) {
    return api.delete(`${baseEndpoint}/deleteSupplier`, {
      params: {
        medereEntity: selectedSuppliers.medereentity
      }
    });
  }

  function getCreditAndDebit(inputText: any, offset: any, pageSize: any) {
    return getMethod({
      basePath: baseEndpoint,
      endpoint: "/getCreditAndDebit",
      params: {
        inputText,
        offset,
        pageSize,
      }
    });
  }

  function getVatCustomerCategories({ filterValue, offSet, pageSize }: GetItemsProps) {
    return getMethod({
      basePath: baseEndpoint,
      endpoint: "/getVatCustomerCategories",
      params: {
        inputText: filterValue ?? "",
        offSet,
        pageSize,
      }
    });
  }

  function getTaxTypes() {
    return getMethod({
      basePath: baseEndpoint,
      endpoint: "/getTaxTypes"
    });
  }

  function getAllAdministrativeFormTypes({ filterValue, offSet, pageSize }: GetItemsProps) {
    return getMethod({
      basePath: baseEndpoint,
      endpoint: "/getAllAdministrativeTypes",
      params: {
        inputText: filterValue,
        offset: offSet,
        pageSize,
      }
    });
  }

  function loadAllPOS({ filterValue, offSet, pageSize, params }: GetItemsProps) {
    return getMethod({
      basePath: baseEndpoint,
      endpoint: "/loadAllPOS",
      params: {
        inputText: filterValue,
        offset: offSet,
        pageSize,
        adminformtype: params.adminformtype,
      }
    });
  }

  function getCompanyEntities(inputText: string, offset: number, pageSize: number, params: any) {
    return postMethod({
      basePath: "/api/secured/profile/profiledataform",
      endpoint: "/getCompanyEntities",
      body: null,
      params: {
        filter: inputText,
        offset,
        limit: pageSize,
        medereentitytag: params.medereentitytag,
      }
    });
  }

  function saveSupplier(generalForm: SupplierForm) {
    const { businessName, commercialName, phone, secondaryPhone, email, city, postalCode, active, invoiceAddress, fiscalDocument, documentType, observations, condIva, credit, subAccounting, paymentMethod, paymentCondition, supplierType, exempt, exemptDate, daysForExpiration, earningWithHoldingConcept } = generalForm;
    return api.post(`${baseEndpoint}`, {
      businessname: businessName,
      commercialname: commercialName,
      phone,
      secondaryphone: secondaryPhone,
      email,
      city: city?.id,
      cityExternalId: city?.isExternalId ?? false,
      cityname: city?.name,
      postalcode: postalCode,
      active: active,
      address: invoiceAddress,
      daysforexpiration: daysForExpiration,
      document: fiscalDocument,
      observations: observations,
      vatidtype: documentType?.documentType,
      vatid: fiscalDocument,
      vatcategory: condIva?.vatcustomercategory,
      invoicebusinessname: businessName,
      invoiceaddress: invoiceAddress,
      posnumber: postalCode,
      credit: credit?.accountingaccount != null ? credit.accountingaccount : null,
      debit: subAccounting?.accountingaccount != null ? subAccounting.accountingaccount : null,
      paymentmethod: paymentMethod?.paymentMethod,
      paymentcondition: paymentCondition?.paymentcondition,
      exempt,
      totime: exemptDate,
      suppliertype: supplierType?.suppliertype,
      earningwithholdingconcept: condIva?.vatcustomercategory !== EVatCustomerCategory.MONOTRIBUTISTA ? earningWithHoldingConcept?.withholdingconcept : null,
    });
  }

  function updateSupplier(generalForm: SupplierForm) {
    const { businessName, commercialName, phone, secondaryPhone, email, city, postalCode, active, invoiceAddress, fiscalDocument, documentType, observations, condIva, credit, subAccounting, paymentMethod, paymentCondition, supplierType, exempt, exemptDate, daysForExpiration, medereentity, earningWithHoldingConcept } = generalForm;
    return api.put(`${baseEndpoint}`, {
      medereentity: medereentity,
      businessname: businessName,
      commercialname: commercialName,
      phone,
      secondaryphone: secondaryPhone,
      email,
      city: city?.id,
      cityExternalId: city?.isExternalId ?? false,
      cityname: city?.name,
      postalcode: postalCode,
      active,
      address: invoiceAddress,
      daysforexpiration: daysForExpiration,
      document: fiscalDocument,
      observations,
      vatidtype: documentType?.documentType,
      vatid: fiscalDocument,
      vatcategory: condIva?.vatcustomercategory,
      invoicebusinessname: businessName,
      invoiceaddress: invoiceAddress,
      posnumber: postalCode,
      credit: credit?.accountingaccount != null ? credit.accountingaccount : null,
      debit: subAccounting?.accountingaccount != null ? subAccounting.accountingaccount : null,
      paymentmethod: paymentMethod?.paymentMethod,
      paymentcondition: paymentCondition?.paymentcondition,
      exempt,
      totime: exemptDate,
      suppliertype: supplierType?.suppliertype,
      earningwithholdingconcept: condIva?.vatcustomercategory !== EVatCustomerCategory.MONOTRIBUTISTA ? earningWithHoldingConcept?.withholdingconcept : null,
    });
  }

  function getPaymentMethods({ filterValue }: GetItemsProps): Promise<PaymentMethod[]> {
    return getMethod<PaymentMethod[]>({
      basePath: baseEndpoint,
      endpoint: "/getPaymentMethods",
      params: { inputText: filterValue ?? "" }
    });
  }

  function getPaymentMethodsForCombo({ filterValue }: GetItemsProps): Promise<ComboDataDTO[]> {
    return getMethod<PaymentMethod[]>({
      basePath: baseEndpoint,
      endpoint: "/getPaymentMethods",
      params: { inputText: filterValue ?? "" }
    }).then((data) => {
      return data.map((pm: PaymentMethod) => ({
        id: pm.paymentMethod,
        name: pm.methodName
      }));
    });
  }

  function getPaymentConditions({ filterValue, offSet, pageSize }: GetItemsProps): Promise<PaymentCondition[]> {
    return getMethod<PaymentCondition[]>({
      basePath: baseEndpoint,
      endpoint: "/getPaymentConditions",
      params: {
        inputText: filterValue ?? "",
        pageSize,
        offSet,
      }
    });
  }

  function getSupplierType({ filterValue, offSet, pageSize }: GetItemsProps): Promise<SupplierTypeDTO[]> {
    return getMethod<SupplierTypeDTO[]>({
      basePath: baseEndpoint,
      endpoint: "/getSupplierTypes",
      params: {
        inputText: filterValue ?? "",
        pageSize,
        offSet,
      }
    });
  }

  function getEarningWithHoldings({ filterValue, offSet, pageSize }: GetItemsProps) {
    return getMethod({
      basePath: baseEndpoint,
      endpoint: "/getEarningWithHoldings",
      params: {
        inputText: filterValue ?? "",
        pageSize,
        offSet,
      }
    });
  }

  function saveIIBB({ percentage, fromTime, toTime }: { percentage: number, fromTime: any, toTime: any }) {
    return getMethod({
      basePath: baseEndpoint,
      endpoint: "/saveArbaIIBB",
      params: {
        percentage,
        fromTime,
        toTime,
      }
    });
  }

  function getArbaCensus({ cuit }: { cuit: string }) {
    return getMethod({
      basePath: baseEndpoint,
      endpoint: "/getArbaCensus",
      params: { cuit }
    });
  }

  function generateAutomaticAccountingMovement({ administrativeFormId }: { administrativeFormId: number }) {
    return getMethod({
      basePath: baseEndpoint,
      endpoint: "/generateAutomaticAccountingMovement",
      params: { administrativeFormId }
    });
  }

  function getAfipStatusByVatCustomerCategory({ offSet, pageSize, params, filterValue }: GetItemsProps<{ vatCustomerCategory?: number }>) {
    return getMethod({
      basePath: baseEndpoint,
      endpoint: "/getAfipStatusByVatCustomerCategory",
      params: {
        offSet,
        pageSize,
        inputtext: filterValue,
        vatCustomerCategory: params?.vatCustomerCategory
      }
    });
  }

  function getMovements(offSet: number, pageSize: number, params: any): Promise<Movement[]> {
    return postMethod<Movement[]>({
      basePath: baseEndpoint,
      endpoint: "/getAllMovements",
      body: {
        loadConfigDTO: {
          offSet,
          pageSize,
          sortingFieldInfoList: params.multiSortMeta,
          filters: params.filterDTO,
        },
        movementStatus: params.movementStatus,
      },
      params: {}
    });
  }

  function getMovementsCombo({ offSet, pageSize, params, filterValue }: GetItemsProps): Promise<Movement[]> {
    return getMethod<Movement[]>({
      basePath: baseEndpoint,
      endpoint: "/getAllMovements",
      params: {
        inputText: filterValue ?? "",
        offSet,
        pageSize,
        supplier: params.supplier,
      }
    });
  }

  function getMovementStatus({ filterValue, offSet, pageSize }: GetItemsProps): Promise<AdministrativeFormStateTypeI18n[]> {
    return getMethod<AdministrativeFormStateTypeI18n[]>({
      basePath: baseEndpoint,
      endpoint: "/getMovementStatus",
      params: {
        filterValue: filterValue ?? "",
        offSet,
        pageSize,
      }
    });
  }

  function getAllSuppliersWithBalance({ filterValue, offSet, pageSize }: GetItemsProps) {
    return getMethod({
      basePath: baseEndpoint,
      endpoint: "/getAllSuppliersWithBalance",
      params: {
        inputText: filterValue ?? "",
        offSet,
        pageSize,
      }
    }).catch(error => {
      if (error.response) {
        console.error("Status:", error.response.status, "Data:", error.response.data);
      }
      throw error;
    });
  }

  return {
    getCities,
    getAllSuppliers,
    getAllSuppliersCombo,
    removeSupplier,
    getCreditAndDebit,
    getVatCustomerCategories,
    getTaxTypes,
    getAllAdministrativeFormTypes,
    loadAllPOS,
    getCompanyEntities,
    saveSupplier,
    updateSupplier,
    getPaymentMethods,
    getPaymentMethodsForCombo,
    getPaymentConditions,
    getSupplierType,
    getEarningWithHoldings,
    saveIIBB,
    getArbaCensus,
    generateAutomaticAccountingMovement,
    getAfipStatusByVatCustomerCategory,
    getMovements,
    getMovementsCombo,
    getMovementStatus,
    getAllSuppliersWithBalance,
  };
}
